import firebase from "firebase/compat/app";
import { isMissionEnabled } from "custom/featuresFlags";
import React, { useContext, useEffect, useState } from "react";
import {
  fetchAllMissions,
  getMissionCoverPhotoUrl
} from "../features/firebase/missions";
import { Mission, MissionFirestoreData } from "../types/Missions";

export type MissionsProviderData = {
  missions?: Mission[];
  refresh: () => Promise<void>;
};

export const MissionsContext = React.createContext<
  MissionsProviderData | undefined
>(undefined);

type Props = {
  children?: React.ReactChild[];
};

const refreshMission = async (currentMissions: Mission[]) => {
  // Fetch a list of all missions from Firestore.
  const allMissions = await fetchAllMissions(firebase.firestore());

  // If we previously fetched a mission's photo, copy it across to our new list.
  let newMissions: Mission[] = allMissions.map(
    (mission: MissionFirestoreData) => {
      return {
        ...mission,
        coverPhotoUrl: currentMissions.find(
          (oldMission) => oldMission.id === mission.id
        )?.coverPhotoUrl
      };
    }
  );

  // Download cover photos for missions which we don't have already.
  for (let mission of newMissions) {
    if (mission.coverPhotoUrl === undefined) {
      try {
        mission.coverPhotoUrl = await getMissionCoverPhotoUrl(mission.id);
      } catch (err) {
        console.error(`Caught error getting mission cover photo URL`);
        console.error(err);
      }
    }
  }

  return newMissions;
};

// We want the provider state to contain:
// - the list of missions
// - a method to refresh

// Initially
// - the missions list is undefined
// - we set useEffect to poll the database after the component loads
export const MissionsProvider = ({ children }: Props) => {
  const updateMissions = async () =>
    setData({
      ...data,
      missions: await refreshMission(data.missions || [])
    });

  const [data, setData] = useState<MissionsProviderData>({
    missions: undefined,
    refresh: updateMissions
  });

  useEffect(() => {
    if (!isMissionEnabled()) return;

    updateMissions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MissionsContext.Provider value={data}>{children}</MissionsContext.Provider>
  );
};

export const useMissions = () => useContext(MissionsContext);
